"use client"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Badge } from "../../../components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Shield, Search, Download, AlertTriangle, CheckCircle, XCircle } from "lucide-react"

const securityLogs = [
  {
    id: 1,
    user: "admin@ministry.com",
    action: "Login",
    status: "success",
    ipAddress: "192.168.1.100",
    location: "New York, NY",
    timestamp: "2024-01-06 14:30:25",
    userAgent: "Chrome 120.0.0.0",
  },
  {
    id: 2,
    user: "john.doe@tso-emails.com",
    action: "Failed Login",
    status: "failed",
    ipAddress: "203.0.113.45",
    location: "Unknown",
    timestamp: "2024-01-06 14:25:12",
    userAgent: "Firefox 121.0.0.0",
  },
  {
    id: 3,
    user: "mary.smith@tso-emails.com",
    action: "Password Reset",
    status: "success",
    ipAddress: "198.51.100.23",
    location: "Los Angeles, CA",
    timestamp: "2024-01-06 13:45:18",
    userAgent: "Safari 17.2.0",
  },
  {
    id: 4,
    user: "suspicious@example.com",
    action: "Multiple Failed Logins",
    status: "blocked",
    ipAddress: "198.51.100.99",
    location: "Unknown",
    timestamp: "2024-01-06 12:15:30",
    userAgent: "Bot/1.0",
  },
  {
    id: 5,
    user: "editor@ministry.com",
    action: "Admin Panel Access",
    status: "success",
    ipAddress: "192.168.1.105",
    location: "New York, NY",
    timestamp: "2024-01-06 11:20:45",
    userAgent: "Chrome 120.0.0.0",
  },
]

export function SecurityLogsSection({allUsers,adminUsers,comments}:any) {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [actionFilter, setActionFilter] = useState("all")

  const filteredLogs = securityLogs.filter((log) => {
    const matchesSearch =
      log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ipAddress.includes(searchTerm) ||
      log.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === "all" || log.status === statusFilter
    const matchesAction = actionFilter === "all" || log.action.toLowerCase().includes(actionFilter.toLowerCase())
    return matchesSearch && matchesStatus && matchesAction
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "success":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Success
          </Badge>
        )
      case "failed":
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        )
      case "blocked":
        return (
          <Badge variant="destructive" className="bg-red-100 text-red-800">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Blocked
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const exportLogs = () => {
    console.log("Exporting security logs...")
  }

  const suspiciousCount = securityLogs.filter((log) => log.status === "failed" || log.status === "blocked").length
  const totalLogins = securityLogs.filter((log) => log.action.toLowerCase().includes("login")).length
  const successRate = totalLogins > 0 ? (((totalLogins - suspiciousCount) / totalLogins) * 100).toFixed(1) : "100"

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium">Success Rate</p>
                <p className="text-2xl font-bold">{successRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm font-medium">Suspicious Activity</p>
                <p className="text-2xl font-bold">{suspiciousCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium">Total Events</p>
                <p className="text-2xl font-bold">{securityLogs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Logs & Login History
              </CardTitle>
              <CardDescription>Monitor login attempts, security events, and user activity</CardDescription>
            </div>
            <Button variant="outline" onClick={exportLogs}>
              <Download className="h-4 w-4 mr-2" />
              Export Logs
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user, IP, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full lg:w-[150px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-full lg:w-[150px]">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="password">Password</SelectItem>
                <SelectItem value="admin">Admin Access</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id} className={log.status === "blocked" ? "bg-red-50" : ""}>
                    <TableCell className="font-medium">{log.user}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{getStatusBadge(log.status)}</TableCell>
                    <TableCell className="font-mono text-sm">{log.ipAddress}</TableCell>
                    <TableCell>{log.location}</TableCell>
                    <TableCell className="text-sm">{log.timestamp}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredLogs.length} of {securityLogs.length} security events
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
